import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  message, 
  Space, 
  Popconfirm,
  Tag,
  Typography,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  GlobalOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { getApiBaseUrl } from '../../../config/apiBase';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const API_BASE_URL = getApiBaseUrl();

const SenderIDBlocking = () => {
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRules();
    fetchUsers();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/admin/global-settings/sender-id-block`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch sender ID rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSaveRule = async (values) => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingRule) {
        const response = await axios.put(
          `${API_BASE_URL}/admin/global-settings/sender-id-block/${editingRule.id}`,
          {
            sender_id: values.sender_ids.split(/[,\n\r]+/).map(k => k.trim()).filter(k => k)[0],
            scope: values.scope,
            user_id: values.scope === 'GLOBAL' ? null : values.user_id,
            reason: values.reason || '',
            is_active: values.is_active
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.warning) {
          message.warning(response.data.warning);
        } else {
          message.success('Rule updated successfully');
        }
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/admin/global-settings/sender-id-block`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.warning) {
          message.warning(response.data.warning);
          if (response.data.duplicates && response.data.duplicates.length > 0) {
            message.info(`Skipped duplicates: ${response.data.duplicates.join(', ')}`);
          }
        } else {
          message.success(`${response.data.inserted || 0} sender ID rule(s) created successfully`);
        }
      }

      setModalVisible(false);
      setEditingRule(null);
      form.resetFields();
      fetchRules();
    } catch (error) {
      if (error.response?.status === 409) {
        message.error(error.response.data.error || 'Duplicate sender ID found');
      } else {
        message.error(error.response?.data?.error || 'Failed to save rule');
      }
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    form.setFieldsValue({
      sender_ids: rule.sender_id,
      scope: rule.scope,
      user_id: rule.user_id,
      reason: rule.reason || '',
      is_active: rule.is_active
    });
    setModalVisible(true);
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/admin/global-settings/sender-id-block/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Rule deactivated successfully');
      fetchRules();
    } catch (error) {
      message.error('Failed to delete rule');
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/admin/global-settings/sender-id-block/${ruleId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Rule status updated');
      fetchRules();
    } catch (error) {
      message.error('Failed to toggle rule');
    }
  };

  const columns = [
    {
      title: 'No',
      key: 'no',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Sender ID / Header',
      dataIndex: 'sender_id',
      key: 'sender_id',
      render: (sid) => <Text code style={{ fontSize: '13px', fontWeight: 600 }}>{sid}</Text>,
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      width: 120,
      render: (scope) => (
        <Tag 
          icon={scope === 'GLOBAL' ? <GlobalOutlined /> : <UserOutlined />}
          color={scope === 'GLOBAL' ? 'red' : 'blue'}
        >
          {scope}
        </Tag>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 150,
      render: (userId) => {
        if (!userId) return '-';
        const user = users.find(u => u.id === userId);
        return user ? user.username : `User ${userId}`;
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (reason) => reason || <Text type="secondary">-</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditRule(record)}
          />
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleRule(record.id)}
          >
            {record.is_active ? 'Disable' : 'Enable'}
          </Button>
          <Popconfirm
            title="Are you sure to deactivate this rule?"
            onConfirm={() => handleDeleteRule(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeGlobal = rules.filter(r => r.scope === 'GLOBAL' && r.is_active).length;
  const activeUser = rules.filter(r => r.scope === 'USER' && r.is_active).length;
  const inactive = rules.filter(r => !r.is_active).length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sender ID / Header Blocking</h1>
        <p className="text-gray-600">
          Block specific Sender IDs (Headers) from being used in SMS submissions. Blocked sender IDs will be rejected at the API level before processing.
        </p>
      </div>

      <Alert
        message="Blocking Behaviour"
        description={
          <div>
            <p><strong>Global</strong> blocks apply system-wide — no user can send SMS with a globally blocked sender ID.</p>
            <p><strong>User-specific</strong> blocks only apply to the selected user.</p>
            <p>Blocked SMS will receive an immediate error response (HTTP 403) and will NOT be queued or billed.</p>
          </div>
        }
        type="info"
        showIcon
        className="mb-6"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-red-500">{activeGlobal}</div>
          <div className="text-gray-500 text-sm">Global Blocks</div>
        </Card>
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-blue-500">{activeUser}</div>
          <div className="text-gray-500 text-sm">User-Specific Blocks</div>
        </Card>
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-gray-400">{inactive}</div>
          <div className="text-gray-500 text-sm">Inactive Rules</div>
        </Card>
      </div>

      <Card>
        <div className="mb-4 flex justify-between items-center">
          <div>
            <Text strong>Total Rules: </Text>
            <Text>{rules.length}</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRule(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Block Sender ID(s)
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rules`,
          }}
        />
      </Card>

      <Modal
        title={editingRule ? 'Edit Sender ID Block Rule' : 'Block Sender ID(s)'}
        open={modalVisible}
        destroyOnClose={true}
        onCancel={() => {
          setModalVisible(false);
          setEditingRule(null);
        }}
        afterClose={() => {
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          key={editingRule ? `edit-${editingRule.id}` : 'new'}
          form={form}
          layout="vertical"
          onFinish={handleSaveRule}
          initialValues={{
            scope: 'GLOBAL',
            is_active: true,
          }}
        >
          <Form.Item
            name="sender_ids"
            label={editingRule ? "Sender ID" : "Sender IDs"}
            rules={[{ required: true, message: `Please enter ${editingRule ? 'a sender ID' : 'sender ID(s)'}` }]}
            tooltip={editingRule ? "Edit the sender ID" : "Enter multiple sender IDs separated by comma or new line. They will be stored in UPPERCASE."}
          >
            <TextArea
              rows={editingRule ? 1 : 4}
              placeholder={editingRule ? "Enter sender ID (e.g., TXTSMS)" : "Enter sender IDs separated by comma or new line\nExample: TXTSMS, HDFCBK, SBIBNK\nor\nTXTSMS\nHDFCBK\nSBIBNK"}
            />
          </Form.Item>

          <Form.Item
            name="scope"
            label="Scope"
            rules={[{ required: true, message: 'Please select scope' }]}
          >
            <Select placeholder="Select scope">
              <Option value="GLOBAL">Global (System-wide)</Option>
              <Option value="USER">User-specific</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.scope !== currentValues.scope
            }
          >
            {({ getFieldValue }) => {
              const scope = getFieldValue('scope');
              if (scope !== 'USER') {
                form.setFieldsValue({ user_id: null });
              }
              
              if (scope === 'USER') {
                return (
                  <Form.Item
                    name="user_id"
                    label="User"
                    rules={[{ required: true, message: 'Please select a user' }]}
                  >
                    <Select 
                      placeholder="Select user"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {users.map(user => (
                        <Option 
                          key={user.id} 
                          value={user.id}
                          label={`${user.username} (${user.email})`}
                        >
                          {user.username} ({user.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason (optional)"
          >
            <Input placeholder="e.g., DND complaint, abuse report, operator request" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SenderIDBlocking;
